import { useCallback, useEffect, useRef } from "react";

export const useDebouncedCallback = <T extends (...args: any[]) => any>(
  callback: T,
  delay = 0,
  unmountAction: "cancel" | "execute" | "ignore" = "execute",
): ((...args: Parameters<T>) => void) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const pendingArgsRef = useRef<Parameters<T> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (unmountAction === "execute" && pendingArgsRef.current !== null) {
        callbackRef.current(...pendingArgsRef.current);
      }
    };
  }, [unmountAction]);

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      pendingArgsRef.current = args;
      timeoutRef.current = setTimeout(() => {
        pendingArgsRef.current = null;
        callback(...args);
      }, delay);
    },
    [callback, delay],
  );
};
